import React, { useState, useRef, useEffect } from 'react';
import {
    Sparkles,
    Send,
    FileText,
    ChevronDown,
    ChevronRight,
    Loader2,
    MessageSquare,
    X,
    PanelRightClose,
    PanelRightOpen,
    Search,
    AlertTriangle,
    FolderSearch,
    Trash2,
    Maximize2,
    Minimize2
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const SourceCard = ({ source }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div style={{
            background: '#f8fafc',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
        }}>
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
                {expanded ? (
                    <ChevronDown size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />
                ) : (
                    <ChevronRight size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />
                )}
                <FileText size={12} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                <span style={{
                    fontSize: '12px',
                    color: '#334155',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    textAlign: 'left'
                }}>{source.file_name}</span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {Math.round(source.similarity_score * 100)}%
                </span>
            </button>
            {expanded && (
                <div style={{ padding: '4px 12px 12px' }}>
                    <p style={{
                        fontSize: '12px',
                        color: '#475569',
                        lineHeight: '1.5',
                        margin: 0
                    }}>{source.chunk_text}</p>
                </div>
            )}
        </div>
    );
};

const AdminDocumentsAIPanel = ({ isOpen, onToggle }) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || isLoading) return;

        const userQuestion = inputMessage.trim();
        setInputMessage('');

        setMessages(prev => [...prev, {
            type: 'user',
            content: userQuestion,
            timestamp: new Date()
        }]);

        setIsLoading(true);

        try {
            const response = await api.post('/files/ai/admin/ask', {
                question: userQuestion
            });

            if (response.data.success) {
                setMessages(prev => [...prev, {
                    type: 'ai',
                    content: response.data.answer,
                    sources: response.data.sources,
                    metadata: response.data.metadata,
                    timestamp: new Date()
                }]);
            }
        } catch (error) {
            console.error('Error asking question:', error);
            setMessages(prev => [...prev, {
                type: 'error',
                content: 'Sorry, I encountered an error processing your question. Please try again.',
                timestamp: new Date()
            }]);
            toast.error('Failed to process question');
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleClearChat = () => {
        setMessages([]);
    };

    const suggestions = [
        "How many documents need review?",
        "Show document library overview",
        "Which documents are overdue?",
        "Summarize our onboarding docs"
    ];

    // Collapsed state
    if (!isOpen) {
        return null;
    }

    // Expanded state — full chat panel
    // Expanded state — full chat panel
    return (
        <>
            {/* Mobile Overlay Backdrop */}
            <div
                className="fixed inset-0 bg-black/20 z-40 xl:hidden"
                style={{ display: window.innerWidth < 1280 ? 'block' : 'none' }}
                onClick={onToggle}
            />

            <div
                className={`ai-panel-container ${isFullScreen ? 'fullscreen-mode' : 'normal-mode'}`}
                style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    animation: 'slideInRight 0.3s ease',
                    zIndex: 50,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                {/* Inline styles for responsive behavior */}
                <style>{`
                    .ai-panel-container {
                        width: 100%;
                        position: fixed;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        height: 100vh;
                        border-radius: 0 !important;
                    }
                    
                    @media (min-width: 1280px) {
                        .ai-panel-container.normal-mode {
                            width: 380px;
                            position: sticky;
                            top: 24px;
                            bottom: auto;
                            height: auto;
                            max-height: calc(100vh - 120px);
                            border-radius: 16px !important;
                        }
                    }

                    .ai-panel-container.fullscreen-mode {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        width: 100vw !important;
                        height: 100vh !important;
                        max-height: 100vh !important;
                        border-radius: 0 !important;
                        z-index: 9999 !important;
                    }

                    @keyframes slideInRight {
                        from { opacity: 0; transform: translateX(20px); }
                        to { opacity: 1; transform: translateX(0); }
                    }
                `}</style>

                {/* Header */}
                <div style={{
                    padding: '16px 20px',
                    background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Sparkles size={16} style={{ color: 'white' }} />
                        </div>
                        <div>
                            <div style={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '15px'
                            }}>Document AI</div>
                            <div style={{
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '11px'
                            }}>Ask about your documents</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {messages.length > 0 && (
                            <button
                                onClick={handleClearChat}
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '8px',
                                    background: 'rgba(255,255,255,0.15)',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                                title="Clear chat"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                        <button
                            onClick={() => setIsFullScreen(!isFullScreen)}
                            style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '8px',
                                background: 'rgba(255,255,255,0.15)',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                            title={isFullScreen ? "Exit fullscreen" : "Fullscreen"}
                        >
                            {isFullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        </button>
                        <button
                            onClick={onToggle}
                            style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '8px',
                                background: 'rgba(255,255,255,0.15)',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                            title="Collapse panel"
                        >
                            <PanelRightClose size={14} />
                        </button>
                    </div>
                </div>

                {/* Messages Area */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    background: 'linear-gradient(180deg, #faf5ff 0%, #f8fafc 100%)',
                    minHeight: '300px'
                }}>
                    {messages.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '24px 16px',
                            flex: 1
                        }}>
                            <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '16px',
                                background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px'
                            }}>
                                <MessageSquare size={24} style={{ color: '#7c3aed' }} />
                            </div>
                            <h3 style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1e293b',
                                margin: '0 0 8px'
                            }}>
                                Ask about your documents
                            </h3>
                            <p style={{
                                fontSize: '13px',
                                color: '#64748b',
                                margin: '0 0 20px',
                                lineHeight: '1.5',
                                maxWidth: '280px'
                            }}>
                                Get insights about your document library, find documents, or check review status.
                            </p>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                width: '100%'
                            }}>
                                {suggestions.map((suggestion, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setInputMessage(suggestion)}
                                        style={{
                                            padding: '10px 14px',
                                            textAlign: 'left',
                                            fontSize: '13px',
                                            color: '#475569',
                                            background: 'white',
                                            borderRadius: '10px',
                                            border: '1px solid #e2e8f0',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.borderColor = '#c4b5fd';
                                            e.currentTarget.style.background = '#faf5ff';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.background = 'white';
                                        }}
                                    >
                                        {i === 0 && <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />}
                                        {i === 1 && <FolderSearch size={14} style={{ color: '#7c3aed', flexShrink: 0 }} />}
                                        {i === 2 && <Search size={14} style={{ color: '#ef4444', flexShrink: 0 }} />}
                                        {i === 3 && <FileText size={14} style={{ color: '#3b82f6', flexShrink: 0 }} />}
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        messages.map((msg, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                            }}>
                                <div style={{ maxWidth: '90%' }}>
                                    {msg.type === 'user' ? (
                                        <div style={{
                                            background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                                            color: 'white',
                                            padding: '10px 14px',
                                            borderRadius: '14px 14px 4px 14px',
                                            boxShadow: '0 2px 8px rgba(124, 58, 237, 0.2)'
                                        }}>
                                            <p style={{
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                margin: 0
                                            }}>{msg.content}</p>
                                        </div>
                                    ) : msg.type === 'error' ? (
                                        <div style={{
                                            background: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            color: '#dc2626',
                                            padding: '10px 14px',
                                            borderRadius: '14px 14px 14px 4px'
                                        }}>
                                            <p style={{
                                                fontSize: '13px',
                                                margin: 0
                                            }}>{msg.content}</p>
                                        </div>
                                    ) : (
                                        <div style={{
                                            background: 'white',
                                            border: '1px solid #e2e8f0',
                                            padding: '12px 14px',
                                            borderRadius: '14px 14px 14px 4px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                marginBottom: '8px'
                                            }}>
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '6px',
                                                    background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Sparkles size={10} style={{ color: 'white' }} />
                                                </div>
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: 500,
                                                    color: '#94a3b8'
                                                }}>Document AI</span>
                                            </div>
                                            <p style={{
                                                fontSize: '13px',
                                                color: '#334155',
                                                lineHeight: '1.6',
                                                margin: 0,
                                                whiteSpace: 'pre-wrap'
                                            }}>{msg.content}</p>

                                            {/* Sources */}
                                            {msg.sources && msg.sources.length > 0 && (
                                                <div style={{
                                                    marginTop: '12px',
                                                    paddingTop: '10px',
                                                    borderTop: '1px solid #f1f5f9'
                                                }}>
                                                    <p style={{
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        color: '#94a3b8',
                                                        margin: '0 0 6px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <FileText size={10} />
                                                        Sources
                                                    </p>
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '4px'
                                                    }}>
                                                        {msg.sources.map((source, i) => (
                                                            <SourceCard key={i} source={source} />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}

                    {isLoading && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                padding: '12px 16px',
                                borderRadius: '14px 14px 14px 4px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <Loader2 size={14} style={{ color: '#7c3aed', animation: 'spin 1s linear infinite' }} />
                                <span style={{ fontSize: '13px', color: '#64748b' }}>Thinking...</span>
                                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e2e8f0',
                    background: 'white',
                    flexShrink: 0
                }}>
                    <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            placeholder="Ask about your documents..."
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '10px 44px 10px 14px',
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                fontSize: '13px',
                                outline: 'none',
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                boxSizing: 'border-box'
                            }}
                            onFocus={e => {
                                e.target.style.borderColor = '#a78bfa';
                                e.target.style.boxShadow = '0 0 0 3px rgba(167, 139, 250, 0.1)';
                            }}
                            onBlur={e => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        <button
                            type="submit"
                            disabled={!inputMessage.trim() || isLoading}
                            style={{
                                position: 'absolute',
                                right: '6px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: (!inputMessage.trim() || isLoading)
                                    ? '#e2e8f0'
                                    : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                                border: 'none',
                                color: 'white',
                                cursor: (!inputMessage.trim() || isLoading) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Send size={14} />
                        </button>
                    </form>
                    <p style={{
                        fontSize: '11px',
                        color: '#94a3b8',
                        margin: '6px 0 0',
                        textAlign: 'center'
                    }}>
                        Answers based on indexed company documents
                    </p>
                </div>
            </div>
        </>
    );
};

export default AdminDocumentsAIPanel;
